import "../i18nFallback";
import { Sentry } from "../sentryInstrumentReact";
import React, { useState, useEffect, useLayoutEffect } from "react";
import Cookies from "js-cookie";
import ReCAPTCHA from "react-google-recaptcha";

import { useCart } from "./CartContext";
import AddressFormSelect from "./AddressFormSelect";
import AddressFormInput from "./AddressFormInput";
import { MONTHS, YEARS } from "./storeLogic/constants";
import { isInDarkMode } from "../util/isInDarkMode";

const darkMode = isInDarkMode();
const colors = darkMode ?
  {
    text: "#fff",
    placeholder: "#B7B7B7",
    backgroundColor: "#242424",
    border: "#444444",
    focus: "#41AEBD",
  }
  :
  {
    text: "#111",
    placeholder: "#ccc",
    border: "#eee",
    backgroundColor: "#fff",
    focus: "#41AEBD",
  };

const urlCSS = `
	body {
		margin:0;
	}

	input[type='tel'] {
		color: ${colors.text};
		margin: 0;
		cursor: pointer;
		border-radius: .35em;
		height: 54px;
		font-family: Helvetica, Arial, sans-serif;
		font-weight: normal;
		padding-top: 22px;
		padding-right:8px;
		padding-bottom: 2px;
		padding-left: .5rem;
		font-size: 1rem;
		line-height: 1em;
		outline-color: ${colors.border};
		border-color: ${colors.border};
		border-width: 2px;
		border-style: solid;
		display: block;
		width: 100%;
		box-sizing: border-box;
		background-color: ${colors.backgroundColor};
		background-image: none;
		appearance: none;
		box-shadow:none;
	}

	input[type='tel']::-moz-placeholder {
		color: ${colors.placeholder};
		font-weight:normal;
		font-family: Helvetica, Arial, sans-serif;
		opacity: 1;
	}

	input[type='tel']:-ms-input-placeholder {
		color: ${colors.placeholder};
		font-weight:normal;
		font-family: Helvetica, Arial, sans-serif;
	}

	input[type='tel']::-webkit-input-placeholder {
		color: ${colors.placeholder};
		font-weight:normal;
		font-family: Helvetica, Arial, sans-serif;
	}

	input[type='tel']::placeholder {
		color: ${colors.placeholder};
		font-weight:normal;
		font-family: Helvetica, Arial, sans-serif;
		opacity: 1;
	}
	
	input[type='tel']:focus {
		outline-color: ${colors.focus};
		border-color: ${colors.focus};
		box-shadow: none;
	}
    input.error {
		border: 2px solid #f54238;
		border-radius: .35rem;
	}
`;
const urlCSSNoSpaces = urlCSS.replace(/\s+/g, "");
const urlCSSEncoded = encodeURIComponent(urlCSSNoSpaces);
const currentYear = new Date().getFullYear();
const currentMonthIndex = new Date().getMonth();

const CHECKOUT_FIELD_ERROR_MAP = {
  "customer.first_name": ["billing", "first_name"],
  "customer.last_name": ["billing", "last_name"],
  "customer.email": ["billing", "email"],
  "customer.phone": ["billing", "phone"],
  "customer.phone.number": ["billing", "phone"],
  "customer.organization_name": ["billing", "organization"],
  "customer.billing_address.street1": ["billing", "street1"],
  "customer.billing_address.street2": ["billing", "street2"],
  "customer.billing_address.city": ["billing", "city"],
  "customer.billing_address.postal_code": ["billing", "post"],
  "customer.billing_address.country": ["country"],
  "customer.billing_address.region": ["region"],
  "customer.shipping_address.first_name": ["shipping", "first_name"],
  "customer.shipping_address.last_name": ["shipping", "last_name"],
  "customer.shipping_address.organization_name": ["shipping", "organization"],
  "customer.shipping_address.street1": ["shipping", "street1"],
  "customer.shipping_address.street2": ["shipping", "street2"],
  "customer.shipping_address.city": ["shipping", "city"],
  "customer.shipping_address.postal_code": ["shipping", "post"],
  "customer.shipping_address.country": ["shipping_country"],
  "customer.shipping_address.region": ["shipping_region"],
  "shipping_rate_id": ["shipping_method"],
  "payment.ccv_number": ["payment", "ccv_number"],
  "payment.expiration.month": ["exp_month"],
  "payment.expiration.year": ["exp_year"],
};

const CHECKOUT_GENERAL_ERROR_KEYS = new Set([
  "cart",
  "recaptcha",
  "customer",
  "payment",
  "customer.detail",
  "payment.expiration",
  "payment.credit_token",
]);

function asErrorList(value) {
  if (!value) {
    return [];
  }
  if (typeof value === "string") {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(asErrorList);
  }
  return [];
}

function isErrorLeaf(value) {
  return (
    typeof value === "string" ||
    (Array.isArray(value) &&
      value.length > 0 &&
      value.every((item) => typeof item === "string"))
  );
}

function flattenApiErrors(errors, prefix = "") {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return [];
  }

  const flattened = [];
  for (const [key, value] of Object.entries(errors)) {
    if (key === "fields") {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;
    if (isErrorLeaf(value)) {
      flattened.push([path, asErrorList(value)]);
      continue;
    }

    if (value && typeof value === "object") {
      flattened.push(...flattenApiErrors(value, path));
    }
  }
  return flattened;
}

function setNestedErrors(target, path, messages) {
  let current = target;
  for (let i = 0; i < path.length - 1; i += 1) {
    current[path[i]] ??= {};
    current = current[path[i]];
  }

  const leaf = path[path.length - 1];
  current[leaf] = [...(current[leaf] || []), ...messages];
}

function mapCheckoutApiErrors(apiErrors) {
  const formErrors = {};
  const generalErrors = [];

  for (const [apiPath, messages] of flattenApiErrors(apiErrors)) {
    if (apiPath === "payment.expiration") {
      setNestedErrors(formErrors, ["exp_month"], messages);
      setNestedErrors(formErrors, ["exp_year"], messages);
      continue;
    }

    const formPath = CHECKOUT_FIELD_ERROR_MAP[apiPath];
    if (formPath) {
      setNestedErrors(formErrors, formPath, messages);
    } else if (CHECKOUT_GENERAL_ERROR_KEYS.has(apiPath)) {
      generalErrors.push(...messages);
    } else {
      generalErrors.push(...messages);
    }
  }

  return { formErrors, generalErrors };
}

function CheckoutPayment() {
  const {
    cart,
    checkoutFormData,
    shipping,
    setCheckoutSubmitting,
    shippingSameAsBilling,
    shippingRequired,
    checkoutErrors,
    setCheckoutErrors,
    clearCheckoutFieldError,
    refreshCart,
    setDiscountNotice,
  } = useCart();
  const [generalError, setGeneralError] = useState("");
  const [scrollToErrors, setScrollToErrors] = useState(false);
  // const [formErrors, setFormErrors] = useState("This is an error");
  const [captchaValue, setCaptchaValue] = useState(null);
  const onCaptchaChange = (value) => setCaptchaValue(value);

  const [paymentFormData, setPaymentFormData] = useState({
    credit_token: "",
    exp_month: currentMonthIndex + 1,
    exp_year: currentYear,
    ccv_number: "",
  });
  const [tokenizerURL, setTokenizerURL] = useState("");
  const csrftoken = Cookies.get("csrftoken");
  const reCaptchaPublicKey = window.RECAPTCHA_PUBLIC_KEY;

  let checkoutDisabled;
  const paymentInfoComplete =
    paymentFormData.exp_month !== "" &&
    paymentFormData.exp_year !== "" &&
    paymentFormData.credit_token !== "" &&
    is3or4DigitNumber(paymentFormData.ccv_number);

  if (captchaValue === null) {
    // if captcha isn't set keep button disabled
    checkoutDisabled = true;
  } else if (
    // shipping required and payment info required
    cart.shipping_required == true &&
    shipping.rateId !== "" &&
    cart.payment_required == true &&
    paymentInfoComplete
  ) {
    checkoutDisabled = false;
  } else if (
    // shipping not required and payment info required
    cart.shipping_required == false &&
    checkoutFormData.billing.addressComplete &&
    cart.payment_required == true &&
    paymentInfoComplete
  ) {
    checkoutDisabled = false;
  } else if (
    // shipping not required and payment not required
    checkoutFormData.billing.addressComplete &&
    cart.shipping_required == false &&
    cart.payment_required == false
  ) {
    checkoutDisabled = false;
  } else {
    // default
    checkoutDisabled = true;
  }

  function is3or4DigitNumber(num) {
    return /^\d{3,4}$/.test(num);
  }

  // get tokenizer url from server
  useEffect(() => {
    async function getTokenizerURL() {
      const cardTitle = encodeURIComponent(
        window.gettext("Credit Card Number")
      );
      // TODO: add error handling
      try {
        const response = await fetch(
          "/payments/_api/tokenizer-url/?method=cardconnect"
        );
        const url = await response.json();
        setTokenizerURL(
          `${url}?enhancedresponse=true&cardnumbernumericonly=true&invalidinputevent=true&cardtitle=${cardTitle}&placeholder=1111 1111 1111 1111&tokenizewheninactive=false&cardinputmaxlength=20&formatinput=true&css=${urlCSSEncoded}`
        );
      } catch (error) {
        if (Sentry && Sentry.getClient()) {
          Sentry.captureException(error, { stage: "getTokenizerURL" });
        }
      }
    }
    getTokenizerURL();

    function handleTokenMessage(event) {
      const iFrame = document.getElementById("tokenFrame");
      if (iFrame?.src.includes(event.origin)) {
        try {
          const data = JSON.parse(event.data);

          setPaymentFormData((prevInfo) => ({
            ...prevInfo,
            credit_token: data.token,
          }));

          console.log(data.validationError, generalError);
          if (data.validationError === "" || data.validationError === undefined) {
            setGeneralError("");
          } else {
			      const errorMessage = data.validationError == "Invalid Account Number" ? "Please enter a valid card number" : data.validationError;
            setGeneralError(errorMessage);
          }
        } catch (error) {
          if (SyntaxError && error instanceof SyntaxError) {
            Sentry.captureException(error, {
              extra: {stage: "parse token data in handleTokenMessage"}
            });
            return;
          }
          throw error;
        }
      }
    }

    window.addEventListener("message", handleTokenMessage);

    return () => {
      window.removeEventListener("message", handleTokenMessage);
    };
  }, []);

  useLayoutEffect(() => {
    if (!scrollToErrors) {
      return;
    }

    setScrollToErrors(false);

    const target =
      document.querySelector(".form-checkout .form-group.error") ||
      document.getElementById("checkout-general-error");

    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target
      .querySelector("input, select, textarea")
      ?.focus({ preventScroll: true });
  }, [scrollToErrors, checkoutErrors]);

  async function formSubmit(e) {
    e.preventDefault();
    setCheckoutSubmitting(true);
    setCheckoutErrors({});
    setGeneralError("");

    const finalCheckoutObject = {
      customer: {
        first_name: checkoutFormData.billing.firstName,
        last_name: checkoutFormData.billing.lastName,
        email: checkoutFormData.billing.email,
        phone: {number: checkoutFormData.billing.phone},
        organization_name: checkoutFormData.billing.organization,
        billing_address: {
          street1: checkoutFormData.billing.street1,
          ...(checkoutFormData.billing.street2 !== ""
            ? { address2: checkoutFormData.billing.street2 }
            : {}),
          city: checkoutFormData.billing.city,
          postal_code: checkoutFormData.billing.postalCode,
          region: checkoutFormData.billing.region,
          country: checkoutFormData.billing.country,
        },
        shipping_same_as_billing: shippingSameAsBilling,
        ...(shippingSameAsBilling === false
          ? {
              shipping_address: {
                first_name: checkoutFormData.shipping.firstName,
                last_name: checkoutFormData.shipping.lastName,
                organization_name: checkoutFormData.shipping.organization,
                street1: checkoutFormData.shipping.street1,
                ...(checkoutFormData.shipping.street2 !== ""
                  ? { address2: checkoutFormData.shipping.street2 }
                  : {}),
                city: checkoutFormData.shipping.city,
                postal_code: checkoutFormData.shipping.postalCode,
                region: checkoutFormData.shipping.region,
                country: checkoutFormData.shipping.country,
              },
            }
          : {}),
      },
      recaptcha: captchaValue,
    };

    // add payment info to finalCheckoutObject if needed
    if (cart.payment_required) {
      finalCheckoutObject.payment = {
        credit_token: paymentFormData.credit_token,
        expiration: {
          month: paymentFormData.exp_month,
          year: paymentFormData.exp_year,
        },
        ccv_number: paymentFormData.ccv_number,
      };
    }

    // add shipping info to finalCheckoutObject if needed
    if (cart.shipping_required) {
      finalCheckoutObject.shipping_rate_id = shipping.rateId;
    }

    try {
      const response = await fetch("/store/_api/cart/checkout/", {
        method: "POST",
        body: JSON.stringify(finalCheckoutObject),
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
      });

      const checkoutResponseObject = await response.json();

      if (!response.ok) {
        const { formErrors, generalErrors } = mapCheckoutApiErrors(
          checkoutResponseObject
        );
        if (checkoutResponseObject.cart) {
          try {
            await refreshCart();
          } catch (refreshError) {
            if (Sentry && Sentry.getClient()) {
              Sentry.captureException(refreshError, {
                stage: "refresh cart after checkout 400",
              });
            }
          }
          const cartMessages = asErrorList(checkoutResponseObject.cart);
          if (cartMessages.some((msg) => /discount/i.test(msg))) {
            setDiscountNotice(cartMessages[0]);
          }
        }
        setCheckoutErrors(formErrors);
        if (generalErrors.length > 0) {
          setGeneralError(generalErrors.join(" "));
        }
        setCheckoutSubmitting(false);
        setScrollToErrors(true);
        return;
      }

      window.location.href = window.gettext("/store/checkout/success/");
    } catch (error) {
      setGeneralError(
        window.gettext(
          "There was an error processing your request. Please try again."
        )
      );
      if (Sentry && Sentry.getClient()) {
        Sentry.captureException(error, { stage: "checkout formSubmit" });
      }
      setCheckoutSubmitting(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === "ccv_number") {
      clearCheckoutFieldError(["payment", "ccv_number"]);
    } else {
      clearCheckoutFieldError([name]);
    }
    setPaymentFormData((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  }

  return (
    <fieldset>
      <div id="payment-preview" className="payment-preview"></div>
      <h2 className="h6">{window.gettext("Payment Information")}</h2>
      {cart.payment_required == true ? (
        <>
          <div className="form-group-row" id="div_id_credit_number">
            <div className="form-group">
              <label className="number-label active" htmlFor="tokenFrame">
                {window.gettext("Credit Card")}
              </label>
              <div id="credit_number_wrap">
                <div id="formtools_credit_number">
                  <input type="hidden" name="credit_token" />
                  <iframe
                    className="cc-iframe"
                    id="tokenFrame"
                    name="tokenFrame"
                    src={tokenizerURL}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-row mb-1">
            <div className="form-group grid-date-cvv" id="div_id_expiration">
              <AddressFormSelect
                containerDivClasses="month"
                labelText={window.gettext("Exp. Month")}
                optionsArray={MONTHS}
                selectName="exp_month"
                selectId="id_expiration_month"
                selectValue={paymentFormData.exp_month}
                onChange={handleChange}
                errors={checkoutErrors.exp_month}
              />
            </div>
            <div className="form-group">
              <AddressFormSelect
                containerDivClasses="year"
                labelText={window.gettext("Exp. Year")}
                optionsArray={YEARS}
                selectName="exp_year"
                selectId="id_expiration_year"
                selectValue={paymentFormData.exp_year}
                onChange={handleChange}
                errors={checkoutErrors.exp_year}
              />
            </div>
            <AddressFormInput
              type="number"
              name="ccv_number"
              label={window.gettext('CVV')}
              value={paymentFormData.ccv_number}
              onChange={handleChange}
              maxLength={4}
              minLength={3}
              min="0"
              placeholder=""
              required={true}
              className="form-control"
              labelClassName="number-label"
              idPrefix="formtools"
              tooltipText={window.gettext(
                "The CVV is the three-digit 'Card Verification Value' code on the back of your credit card. It's usually just to the right of the signature box."
              )}
              errors={checkoutErrors?.payment?.ccv_number ?? ""}
            />
          </div>
        </>
      ) : (
        <p>Payment information not required</p>
      )}
      <ReCAPTCHA sitekey={reCaptchaPublicKey} onChange={onCaptchaChange} size="compact" theme={darkMode ? "dark" : "light"} />
      {generalError !== "" && (
        <p className="error" id="checkout-general-error">
          {generalError}
        </p>
      )}
      <div className="btn-group">
        <input
          type="submit"
          className="btn btn-primary btn-lg btn-spotlight mt-1"
          onClick={formSubmit}
          value={window.gettext("Purchase")}
          disabled={checkoutDisabled}
        />
      </div>
    </fieldset>
  );
}

export default CheckoutPayment;
